import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signInAnonymously as fbSignInAnonymously, signInWithPopup, signOut as fbSignOut, onAuthStateChanged, updateProfile, updateEmail, updatePassword, sendPasswordResetEmail, sendEmailVerification as fbSendEmailVerification, reload, reauthenticateWithCredential, getAdditionalUserInfo, linkWithCredential, linkWithPopup, unlink, EmailAuthProvider, GoogleAuthProvider, OAuthProvider, } from "firebase/auth";
import { serverTimestamp, setDoc } from "firebase/firestore";
import { asUserId, err, ok } from "@pack-it-up/shared";
const toAuthUser = (u) => ({
    id: asUserId(u.uid),
    email: u.email ?? "",
    displayName: u.displayName ?? "",
    ...(u.photoURL ? { photoUrl: u.photoURL } : {}),
    isAnonymous: u.isAnonymous,
    providers: u.providerData.map((p) => p.providerId),
    emailVerified: u.emailVerified,
});
const mapAuthError = (e) => {
    const code = e.code ?? "";
    switch (code) {
        case "auth/wrong-password":
        case "auth/invalid-credential":
            return "WrongPassword";
        case "auth/email-already-in-use":
            return "EmailInUse";
        case "auth/weak-password":
            return "WeakPassword";
        case "auth/requires-recent-login":
            return "RequiresRecentLogin";
        case "auth/user-not-found":
            return "UserNotFound";
        case "auth/popup-closed-by-user":
        case "auth/cancelled-popup-request":
        case "auth/popup-blocked":
            return "PopupClosed";
        case "auth/account-exists-with-different-credential":
        case "auth/credential-already-in-use":
            return "AccountExistsWithDifferentCredential";
        case "auth/operation-not-allowed":
            return "ProviderDisabled";
        default:
            return "Network";
    }
};
// Firebase popup provider for a domain AuthProvider. "password" has no popup flow —
// callers treat it as an unavailable provider.
const toFirebaseProvider = (p) => {
    switch (p.kind) {
        case "password":
            return null;
        case "google":
            return new GoogleAuthProvider();
        case "apple":
            return new OAuthProvider("apple.com");
        case "oidc":
            return new OAuthProvider(p.providerId);
    }
};
export const makeAuthRepo = (auth, db, p) => {
    const reauth = async (currentPassword) => {
        const u = auth.currentUser;
        if (!u || !u.email)
            throw { code: "auth/requires-recent-login" };
        await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email, currentPassword));
        return u;
    };
    const hasPasswordProvider = (u) => u.providerData.some((d) => d.providerId === "password");
    // Private profile doc + the defaults captured at signup (Doc 02 §8, Doc 09 §1).
    // Timezone defaults to the device zone (the reminder boundary uses it, Doc 09 §4);
    // the user can change it later in Settings.
    const writeSignupProfileDoc = async (u, displayName, email) => {
        const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        await setDoc(p.user(u.uid), {
            userId: u.uid,
            displayName,
            email,
            searchKey: email.trim().toLowerCase(),
            timezone: deviceTimezone,
            reminderSendHour: 7,
            createdAt: serverTimestamp(),
        });
    };
    return {
        observeCurrentUser: () => ({
            subscribe(next) {
                const unsub = onAuthStateChanged(auth, (u) => next(u ? toAuthUser(u) : null));
                return unsub;
            },
        }),
        async signUp(email, password, displayName) {
            try {
                const cred = await createUserWithEmailAndPassword(auth, email, password);
                await updateProfile(cred.user, { displayName });
                await writeSignupProfileDoc(cred.user, displayName, email);
                return ok(toAuthUser(cred.user));
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
        async signIn(email, password) {
            try {
                const cred = await signInWithEmailAndPassword(auth, email, password);
                return ok(toAuthUser(cred.user));
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
        // Anonymous session for view-via-link (Doc 08 §4a). No users/{uid} profile doc is
        // written — an anonymous viewer has no profile to read and stays invisible to the
        // trip's members; upgrading to a real account later goes through account linking.
        async signInAnonymously() {
            try {
                const cred = await fbSignInAnonymously(auth);
                return ok(toAuthUser(cred.user));
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
        // T31 — auth-profile-only name, used by an anonymous guest. Firebase persists it
        // with the session (same storage as the anonymous uid), so the next link the same
        // browser opens can prefill the name instead of asking again.
        async setAuthDisplayName(displayName) {
            const u = auth.currentUser;
            if (u === null)
                return err("RequiresRecentLogin");
            try {
                await updateProfile(u, { displayName });
                return ok(toAuthUser(u));
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
        async signOut() {
            await fbSignOut(auth);
        },
        async changeEmail(newEmail, currentPassword) {
            try {
                const u = await reauth(currentPassword);
                await updateEmail(u, newEmail);
                return ok(undefined);
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
        async changePassword(currentPassword, newPassword) {
            const current = auth.currentUser;
            if (current !== null && !hasPasswordProvider(current))
                return err("NoPasswordCredential");
            try {
                const u = await reauth(currentPassword);
                await updatePassword(u, newPassword);
                return ok(undefined);
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
        // Links an email+password credential to a provider-only account (the address
        // already on the account), so the OAuth provider can later be unlinked. With a
        // password provider already linked, Firebase rejects (provider-already-linked)
        // — the UI gates on `providers` and routes to changePassword instead.
        async setPassword(newPassword) {
            const u = auth.currentUser;
            if (u === null || u.email === null || u.email === "")
                return err("RequiresRecentLogin");
            try {
                const cred = await linkWithCredential(u, EmailAuthProvider.credential(u.email, newPassword));
                return ok(toAuthUser(cred.user));
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
        async sendPasswordReset(email) {
            try {
                await sendPasswordResetEmail(auth, email);
                return ok(undefined);
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
        async sendEmailVerification() {
            const u = auth.currentUser;
            if (u === null)
                return err("RequiresRecentLogin");
            try {
                await fbSendEmailVerification(u);
                return ok(undefined);
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
        async refreshCurrentUser() {
            const u = auth.currentUser;
            if (u === null)
                return err("RequiresRecentLogin");
            try {
                await reload(u);
                return ok(toAuthUser(u));
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
        // --- Federated/OIDC provider flows (Doc 04 §1). Popup-based: redirects lose
        // app state and popups keep the trip screen mounted. ---
        async signInWithProvider(provider) {
            const fbProvider = toFirebaseProvider(provider);
            if (fbProvider === null)
                return err("ProviderDisabled");
            try {
                const cred = await signInWithPopup(auth, fbProvider);
                // First use doubles as sign-up: leave the same users/{uid} doc a password
                // signup writes, so friend lookup and reminders work for OIDC accounts.
                if (getAdditionalUserInfo(cred)?.isNewUser === true) {
                    await writeSignupProfileDoc(cred.user, cred.user.displayName ?? "", cred.user.email ?? "");
                }
                return ok(toAuthUser(cred.user));
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
        async linkProvider(provider) {
            const u = auth.currentUser;
            if (u === null)
                return err("RequiresRecentLogin");
            const fbProvider = toFirebaseProvider(provider);
            if (fbProvider === null)
                return err("ProviderDisabled");
            try {
                const cred = await linkWithPopup(u, fbProvider);
                return ok(toAuthUser(cred.user));
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
        async unlinkProvider(providerId) {
            const u = auth.currentUser;
            if (u === null)
                return err("RequiresRecentLogin");
            // Never strand the account: the last sign-in method cannot be removed.
            if (u.providerData.length <= 1)
                return err("LastSignInMethod");
            try {
                const updated = await unlink(u, providerId);
                return ok(toAuthUser(updated));
            }
            catch (e) {
                return err(mapAuthError(e));
            }
        },
    };
};
//# sourceMappingURL=auth.repo.js.map